import { StatusData } from "@fairfleet/geotab";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { subDays } from "date-fns";
import { useGeotabClient } from "@/components/GeotabClientContext";
import { fetchStatusDataComplete } from "@/utilities/query/fetchStatusDataComplete";

/**
 * How often the day is refetched.
 *
 * Far less often than the live window: a day-long window barely moves from one minute to the
 * next, and it is a much larger query.
 */
const REFETCH_MS = 5 * 60 * 1000;

/**
 * The newest reading of every diagnostic a vehicle reported in the last 24 hours.
 *
 * Paged rather than fetched in one call. Geotab returns a date range oldest first and silently
 * truncates it at the results limit, so a single request for a whole day of a busy vehicle
 * returns the *oldest* slice of it — the checklist would then report "not reported" for values
 * that arrived an hour ago, which is the exact opposite of what it is for.
 *
 * Only the newest reading per diagnostic is kept. That is all the checklist ever asks for, and
 * holding a full day of readings for one vehicle in memory is tens of thousands of objects.
 */
export function useDayStatusData(deviceId: string | undefined) {
  const { geotabClient, activeSessionId } = useGeotabClient();

  return useQuery({
    queryKey: ["dayStatusData", activeSessionId, deviceId],
    queryFn: async ({ signal }) => {
      const toDate = new Date();
      const fromDate = subDays(toDate, 1);
      const newest = new Map<string, { record: StatusData; ms: number }>();

      await fetchStatusDataComplete(geotabClient, {
        tasks: [{ key: "day", deviceId: deviceId as string }],
        fromDate,
        toDate,
        signal,
        onPage: (_key, page) => keepNewest(newest, page),
      });

      return [...newest.values()].map(entry => entry.record);
    },
    enabled: !!deviceId,
    refetchInterval: REFETCH_MS,
    placeholderData: keepPreviousData,
  });
}

/**
 * Keeps the newest record per diagnostic.
 *
 * The timestamp is kept alongside the record rather than re-read from it: the client already
 * turns it into a `Date`, and a diagnostic reported thousands of times a day would otherwise
 * have the incumbent's timestamp read again for every challenger.
 */
function keepNewest(
  newest: Map<string, { record: StatusData; ms: number }>,
  page: StatusData[],
): void {
  for (const record of page) {
    const ms = record.dateTime.getTime();
    // An unreadable timestamp must not displace a record with a usable one.
    if (!Number.isFinite(ms)) {
      continue;
    }

    const key = record.diagnostic?.id ?? "";
    const previous = newest.get(key);
    if (previous === undefined || ms > previous.ms) {
      newest.set(key, { record, ms });
    }
  }
}
